import {
  Grid,
  GridCellProps,
  GridNoRecords,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridSelectionChangeEvent
} from '@progress/kendo-react-grid';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import { useSessionStorage } from 'usehooks-ts';
import { Button } from '@progress/kendo-react-buttons';
import { Checkbox } from '@progress/kendo-react-inputs';
import { process, State } from '@progress/kendo-data-query';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { Window, WindowActionsBar } from '@progress/kendo-react-dialogs';

import Toolbar from '../toolbar';
import {
  AddResourceAsync,
  GetResourceById,
  GetResourceValue,
  SaveEntityEditorAsync,
  UpdateGameDocState,
  UpdateResourceAsync
} from '../../../utils/game-document';
import {
  TaskEntity,
  ResourceEntity,
  TaskContentEntity
} from '../../../types/game-document/';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../../../components/grid/grid-tool-bar';
import { LinkCell } from '../../../components/grid';
import { uuid } from '../../../types/common-helper';
import { getCurrentTimeStamp } from '../../../utils/date';
import {
  ExportTaskContents,
  ImportTaskContents,
  AddTaskContentAsync,
  CopyTaskContentAsync,
  DeleteTaskContentAsync,
  UpdateTaskContentAsync
} from '../../../utils/game-document/assets/task-contents';
import { useToast } from '../../../contexts/toast-context';
import { GameDocument } from '../../../types/game-document';
import { useBreadcrumb } from '../../../contexts/breadcrumb';
import { NoRecords } from '../../../components/grid/no-records';
import YesNoDialog from '../../../components/dialogs/yes-no-dialog';
import { GameDocumentContext } from '../../../contexts/game-document';
import { downloadJSON, handleFileUpload } from '../../../utils/files';
import { DefaultGridSettings } from '../../../constants/grid-settings';
import { EntityEditor } from '../../../types/game-document/entity-editor';
import { isContentHtmlEmpty } from '../../../utils/game-document/html-content';
import EditDeleteCloneCell from '../../../components/grid/edit-delete-clone-cell';
import { TaskContentEditorWindow } from '../../../features/game-document/task-contents';
import { ExportedTaskContentData } from '../../../types/game-document/entities/exported-task-content';

const initialDataState: State = {
  sort: [{ field: 'title', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

interface TaskContentGridEntity extends TaskContentEntity {
  selected: boolean;
}

const SELECTED_FIELD = 'selected';

const TaskContents = () => {
  const navigate = useNavigate();
  const [state, setState] = useContext(GameDocumentContext);
  const [dataState, setDataState] = useSessionStorage<State>(
    'datastate-task-contents',
    initialDataState
  );

  const [filteredTaskContent, setFilteredTaskContent] = useState<
    TaskContentGridEntity[] | undefined
  >([]);
  const [selectedTaskContentIds, setSelectecTaskContentIds] = useState<
    string[]
  >([]);
  const [searchText, setSearchText] = useSessionStorage<string>(
    'searchstate-task-contents',
    ''
  );
  const { onToastOpen } = useToast();

  type DeleteContext =
    | { mode: 'single'; id: string }
    | { mode: 'bulk'; ids: string[] };

  const [showConfirmationDelete, setShowConfirmationDelete] =
    useState<DeleteContext | null>(null);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [pendingImport, setPendingImport] =
    useState<ExportedTaskContentData | null>(null);
  const [importCreateTask, setImportCreateTask] = useState(true);

  useEffect(() => {
    // Set the page task.
    document.title = `Task Content - ${state.gameDocument?.name}`;
    const taskContents = state.gameDocument?.assets?.taskContents!.filter(
      (x) => x.id !== '-'
    ) as TaskContentGridEntity[] | undefined;

    setFilteredTaskContent(
      taskContents?.map((tc) => ({
        ...tc,
        title: GetResourceValue(state.gameDocument!, tc.titleResId ?? ''),
        formCount: tc.forms?.length ?? 0,
        preMessage: isContentHtmlEmpty(
          GetResourceById(state.gameDocument!, tc.preMessageResId ?? '')
            ?.value ?? ''
        )
          ? 'No'
          : 'Yes',
        intro: isContentHtmlEmpty(
          GetResourceById(state.gameDocument!, tc.contentResId ?? '')?.value ??
            ''
        )
          ? 'No'
          : 'Yes',
        odt: tc.onDemandTaskCategory !== undefined ? 'Yes' : 'No',
        odtCategory: tc.onDemandTaskCategory ?? '-',
        ar: tc.ARMiniGame !== undefined ? 'Yes' : 'No'
      }))
    );
  }, [state]);

  const handleJsonExport = async () => {
    if (selectedTaskContentIds.length === 0) {
      onToastOpen({
        title: 'Error',
        body: 'Please select the task content you want to export first.',
        style: 'error'
      });

      return;
    }
    const exportedTaskContents = ExportTaskContents(
      state.gameDocument!,
      selectedTaskContentIds
    );
    const formattedTimestamp = getCurrentTimeStamp();

    downloadJSON(
      exportedTaskContents,
      `task-content-${formattedTimestamp}.json`
    );
  };

  const handleJsonImport = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleFileUpload<ExportedTaskContentData>(
      event,
      async (exportedTaskContent) => {
        // Store parsed data and show confirmation dialog
        setPendingImport(exportedTaskContent);
      },
      (message) => {
        onToastOpen({
          title: 'Error',
          body: message,
          style: 'error'
        });
      }
    );
  };

  const handleConfirmImport = async () => {
    if (!pendingImport) return;
    try {
      const updatedGameDocument = await ImportTaskContents(
        state.gameDocument!,
        pendingImport,
        importCreateTask
      );
      setState((state) => UpdateGameDocState(state, updatedGameDocument));
      onToastOpen({
        title: 'Info',
        body: 'Import Success',
        style: 'info'
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Import failed. No changes were applied.';
      onToastOpen({
        title: 'Error',
        body: errorMessage,
        style: 'error'
      });
    } finally {
      setPendingImport(null);
      setImportCreateTask(true);
    }
  };

  const handleCancelImport = () => {
    setPendingImport(null);
    setImportCreateTask(true);
  };

  const handleBulkDelete = async () => {
    if (selectedTaskContentIds.length === 0) {
      onToastOpen({
        title: 'Error',
        body: 'Please select the task content you want to delete first.',
        style: 'error'
      });

      return;
    }

    setShowConfirmationDelete({ mode: 'bulk', ids: selectedTaskContentIds });
    setConfirmationMessage(
      `Are you sure you want to remove these ${selectedTaskContentIds.length} task contents?`
    );
  };

  const LinkedNameCell = (props: GridCellProps) => (
    <LinkCell to={props.dataItem['id'].toString()} {...props} />
  );

  const DeleteCell = (props: GridCellProps) => (
    <EditDeleteCloneCell
      onEditClick={() => onEditEntity(props.dataItem['id'])}
      onCloneClick={() => onCloneEntity(props.dataItem['id'])}
      onDeleteClick={() => {
        setShowConfirmationDelete({ mode: 'single', id: props.dataItem['id'] });
        setConfirmationMessage(
          'Are you sure you want to remove the task content?'
        );
      }}
      {...props}
    />
  );

  const DescriptionCell = (props: GridCellProps) => {
    const descriptions = props.dataItem?.description;
    if (descriptions.length <= 250) {
      return (
        <td role={'gridcell'} className={'k-table-td'}>
          {descriptions}
        </td>
      );
    }
    return (
      <td title={descriptions} role={'gridcell'} className={'k-table-td'}>
        {descriptions.slice(0, 250)}...
      </td>
    );
  };

  const [entityEditorValue, setEntityEditorValue] =
    useState<EntityEditor<TaskContentEntity>>();
  const [entityEditorIsVisible, setEntityEditorIsVisible] =
    useState<boolean>(false);
  const toggleEntityEditor = () => {
    setEntityEditorIsVisible(!entityEditorIsVisible);
  };

  const handleEntityEditorSubmit = async (
    editorEntity: EntityEditor<TaskContentEntity>,
    createTask?: boolean,
    title?: string
  ) => {
    if (editorEntity.isNew) {
      const titleGuid = uuid(); //titleResId
      const preMessageGuid = uuid(); //preMessageResId
      const contentGuid = uuid(); //contentResId
      const taskContentId = uuid();

      const addTitleResource = AddResourceAsync(
        state.gameDocument!,
        'Title',
        '',
        'text',
        title ?? '',
        titleGuid
      );
      const addContentResource = AddResourceAsync(
        state.gameDocument!,
        'Content',
        '',
        'text-md',
        '',
        contentGuid
      );
      const addPreMessageResource = AddResourceAsync(
        state.gameDocument!,
        'PreMessage',
        '',
        'text-md',
        '',
        preMessageGuid
      );
      Promise.all([
        addTitleResource,
        addContentResource,
        addPreMessageResource
      ]).then(async () => {
        const updatedGameDocument = await AddTaskContentAsync(
          state.gameDocument!,
          editorEntity.entity.name,
          editorEntity.entity.description,
          editorEntity.entity.bodyType!,
          titleGuid,
          preMessageGuid,
          contentGuid,
          taskContentId
        );

        let finalGameDocument: GameDocument = updatedGameDocument;

        if (createTask) {
          // Create a Task from the Task Content metadata
          const taskTitleGuid = uuid();
          const defaultTaskAvailableIconResId =
            state.gameDocument?.settings.designer
              ?.defaultTaskAvailableIconResId || '';
          const defaultTaskCompletedIconResId =
            state.gameDocument?.settings.designer
              ?.defaultTaskCompletedIconResId || '';

          const taskEntity: TaskEntity = {
            id: '',
            name: editorEntity.entity.name,
            description: editorEntity.entity.description,
            titleResId: taskTitleGuid,
            imageResId: defaultTaskAvailableIconResId,
            completeImageResId: defaultTaskCompletedIconResId,
            taskContentId: taskContentId,
            hideWhenComplete:
              state.gameDocument && !state.gameDocument.settings.designer
                ? false
                : !Boolean(
                    state.gameDocument?.settings.designer
                      ?.taskCompletionVisibility
                  )
          };

          const taskTitleResource: ResourceEntity = {
            id: taskTitleGuid,
            name: 'Title',
            description: '',
            type: 'text',
            value: title ?? ''
          };

          const taskEditorEntity: EntityEditor<TaskEntity> = {
            isNew: true,
            entity: taskEntity
          };

          const taskResourceEntities: EntityEditor<ResourceEntity>[] = [
            { isNew: true, entity: taskTitleResource }
          ];

          const docWithTask = await SaveEntityEditorAsync(
            finalGameDocument,
            taskEditorEntity,
            taskResourceEntities
          );

          if (docWithTask) {
            finalGameDocument = docWithTask as GameDocument;
          }
        }

        setState((state) => UpdateGameDocState(state, finalGameDocument));
        setEntityEditorIsVisible(false);
        navigate(
          `/designer/${state.gameId}/assets/task-content/${taskContentId}`
        );
      });
    } else {
      UpdateTaskContentAsync(
        state.gameDocument!,
        editorEntity.entity.id,
        editorEntity.entity
      ).then((updatedGameDocument) => {
        let result = updatedGameDocument as GameDocument;
        if (title) {
          const titleRes = GetResourceById(
            state.gameDocument!,
            editorEntity.entity.titleResId ?? ''
          );
          if (titleRes) {
            titleRes.value = title;
            UpdateResourceAsync(
              state.gameDocument!,
              titleRes.id,
              titleRes
            ).then((response) => {
              result = response;
            });
          }
        }

        setState((state) => UpdateGameDocState(state, result));
        setEntityEditorIsVisible(false);
      });
    }
  };

  const onAddEntity = () => {
    setEntityEditorValue({
      isNew: true,
      entity: { id: '', name: '', description: '' }
    });
    toggleEntityEditor();
  };

  const onEditEntity = (entityId: string) => {
    setEntityEditorValue({
      isNew: false,
      entity: state.gameDocument?.assets.taskContents?.find(
        (i) => i.id === entityId
      )!
    });
    toggleEntityEditor();
  };

  const onCloneEntity = (entityId: string) => {
    CopyTaskContentAsync(state?.gameDocument!, entityId).then((response) => {
      setState((state) => UpdateGameDocState(state, response));
    });
  };

  const onDeleteEntity = async (entityId: string) => {
    DeleteTaskContentAsync(state.gameDocument!, entityId).then(
      (updatedGameDocument) => {
        setState((state) => UpdateGameDocState(state, updatedGameDocument));
        setShowConfirmationDelete(null);
      }
    );
  };

  const onDeleteEntities = async (entityIds: string[]) => {
    if (entityIds?.length) {
      for (const id of entityIds) {
        const updatedDoc = await DeleteTaskContentAsync(
          state.gameDocument!,
          id
        );
        setState((state) => UpdateGameDocState(state, updatedDoc));
      }

      setShowConfirmationDelete(null);
      setSelectecTaskContentIds([]);
    }
  };

  const onSelectionChange = (event: GridSelectionChangeEvent) => {
    if (!event.dataItem) return;
    const selectedTaskContentId = event.dataItem['id'];

    const updatedTaskContents = filteredTaskContent?.map((item) =>
      item.id === selectedTaskContentId
        ? { ...item, selected: !item.selected }
        : item
    );
    setFilteredTaskContent(updatedTaskContents);

    const taskContent = updatedTaskContents?.find(
      (item) => item.id === selectedTaskContentId
    );

    if (taskContent) {
      const updatedSelectedTaskContentIds = taskContent.selected
        ? [...selectedTaskContentIds, selectedTaskContentId]
        : selectedTaskContentIds.filter((id) => id !== selectedTaskContentId);

      setSelectecTaskContentIds(updatedSelectedTaskContentIds);
    }
  };

  const onHeaderSelectionChange = () => {
    const updatedTaskContents = filteredTaskContent?.map((item) => ({
      ...item,
      selected: !item.selected
    }));

    const updatedSelectedTaskContentIds = updatedTaskContents?.reduce<string[]>(
      (ids, item) => {
        if (item.selected) {
          return [...ids, item.id];
        } else {
          return ids.filter((id) => id !== item.id);
        }
      },
      [...(selectedTaskContentIds || [])]
    );

    setSelectecTaskContentIds(updatedSelectedTaskContentIds || []);
    setFilteredTaskContent(updatedTaskContents);
  };

  const onExportExcel = async () => {
    try {
      const taskContents = filteredTaskContent;

      const formattedData = taskContents!.flatMap((task: any) => {
        // If task has no forms, still return a default record
        if (!task.forms || task.forms.length === 0) {
          return [
            {
              task_id: task.id,
              task_description: task.description,
              bodyType: task.bodyType,
              title:
                GetResourceById(state?.gameDocument!, task.titleResId)?.value ||
                '',
              content:
                stripHtmlToText(
                  GetResourceById(state?.gameDocument!, task.contentResId)
                    ?.value
                ) || '',
              preMessage:
                stripHtmlToText(
                  GetResourceById(state?.gameDocument!, task.preMessageResId)
                    ?.value
                ) || '',
              form_id: null,
              form_type: null,
              isManualScoring: null,
              question: null,
              score: '',
              answer: null,
              isChecked: ''
            }
          ];
        }

        // Map forms and answers as before
        return task.forms.flatMap((form: any) => {
          const answers = form.answers || [];

          // If no answers, still return form-level info
          if (answers.length === 0) {
            return [
              {
                task_id: task.id,
                task_description: task.description,
                bodyType: task.bodyType,
                title:
                  GetResourceById(state?.gameDocument!, task.titleResId)
                    ?.value || '',
                content:
                  stripHtmlToText(
                    GetResourceById(state?.gameDocument!, task.contentResId)
                      ?.value
                  ) || '',
                preMessage:
                  stripHtmlToText(
                    GetResourceById(state?.gameDocument!, task.preMessageResId)
                      ?.value
                  ) || '',
                form_id: form.id,
                form_type: form.type,
                isManualScoring: form.isManualScoring,
                question:
                  GetResourceById(state?.gameDocument!, form.questionResId)
                    ?.value || '',
                score: form.score || '',
                answer: null,
                isChecked: ''
              }
            ];
          }

          // Special handling for numeric type forms
          if (form.type === 'number' && answers.length === 2) {
            const minAnswer =
              GetResourceById(state?.gameDocument!, answers[0].answerResId)
                ?.value || '';
            const maxAnswer =
              GetResourceById(state?.gameDocument!, answers[1].answerResId)
                ?.value || '';

            return [
              {
                task_id: task.id,
                task_description: task.description,
                bodyType: task.bodyType,
                title:
                  GetResourceById(state?.gameDocument!, task.titleResId)
                    ?.value || '',
                content:
                  stripHtmlToText(
                    GetResourceById(state?.gameDocument!, task.contentResId)
                      ?.value
                  ) || '',
                preMessage:
                  stripHtmlToText(
                    GetResourceById(state?.gameDocument!, task.preMessageResId)
                      ?.value
                  ) || '',
                form_id: form.id,
                form_type: form.type,
                isManualScoring: form.isManualScoring,
                question:
                  GetResourceById(state?.gameDocument!, form.questionResId)
                    ?.value || '',
                score: form.score || '',
                answer:
                  minAnswer === maxAnswer
                    ? `${minAnswer}`
                    : `${minAnswer} - ${maxAnswer}`, // Single number or combine as range
                isChecked: answers[0].isChecked || answers[1].isChecked || ''
              }
            ];
          }

          // Normal case: has answers
          return answers.map((answer: any) => ({
            task_id: task.id,
            task_description: task.description,
            bodyType: task.bodyType,
            title:
              GetResourceById(state?.gameDocument!, task.titleResId)?.value ||
              '',
            content:
              stripHtmlToText(
                GetResourceById(state?.gameDocument!, task.contentResId)?.value
              ) || '',
            preMessage:
              stripHtmlToText(
                GetResourceById(state?.gameDocument!, task.preMessageResId)
                  ?.value
              ) || '',
            form_id: form.id,
            form_type: form.type,
            isManualScoring: form.isManualScoring,
            question:
              GetResourceById(state?.gameDocument!, form.questionResId)
                ?.value || '',
            score: form.score || answer.score || '',
            answer:
              GetResourceById(state?.gameDocument!, answer.answerResId)
                ?.value || '',
            isChecked: answer.isChecked || ''
          }));
        });
      });

      if (gridExportRef.current !== null) {
        gridExportRef.current.save(formattedData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const stripHtmlToText = (html: string | undefined) => {
    if (!html) return;
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return (doc.body.textContent || '').trim();
  };

  const gridExportRef = useRef<ExcelExport | null>(null);

  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();

  useEffect(() => {
    const breadcrumbPaths = [
      { path: '/designer/:gameId/assets/*', text: 'Assets' },
      { path: '/designer/:gameId/assets/task-content/*', text: 'Task Content' }
    ];

    const newBreadcrumb = [];
    for (const config of breadcrumbPaths) {
      const match = matchPath(config.path, location.pathname);

      if (match) {
        newBreadcrumb.push({ text: config.text, to: match.pathnameBase });
      }
    }

    setBreadcrumb(newBreadcrumb);

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname]);

  return (
    <>
      <Toolbar
        title={'Task Content'}
        buttonHelpSupport={{
          title: 'Task Content guide',
          url: 'https://forum.catalystglobal.com/t/4526'
        }}>
        <Button onClick={onAddEntity} themeColor={'primary'}>
          Add task content
        </Button>
        {entityEditorIsVisible && (
          <TaskContentEditorWindow
            toggleDialog={toggleEntityEditor}
            onSubmit={handleEntityEditorSubmit}
            onClose={toggleEntityEditor}
            editorEntity={entityEditorValue!}
            editorMode={'basic'}
          />
        )}
      </Toolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search task content'}
          columnsToSearch={['title', 'description', 'onDemandTaskCategory']}
          showCardMode={false}
          exportRef={gridExportRef}
          showJsonExport={true}
          handleJsonExport={handleJsonExport}
          showJsonImport={true}
          handleJsonImport={handleJsonImport}
          useCustomExportExcel={true}
          showBulkDelete={true}
          handleBulkDelete={handleBulkDelete}
          handleCustomExportToExcel={onExportExcel}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}
          searchTerm={searchText}
          onSearchChange={(searchTerm) => {
            setSearchText(searchTerm);
          }}
        />
        <ExcelExport
          data={filteredTaskContent ?? []}
          ref={gridExportRef}
          fileName={`${state.gameDocument?.name} task contents.xlsx`}>
          <ExcelExportColumn field="task_id" title="Task Content ID" />
          <ExcelExportColumn field="title" title="Title" />
          <ExcelExportColumn field="task_description" title="Description" />
          <ExcelExportColumn field="bodyType" title="Body Type" />
          <ExcelExportColumn field="content" title="Intro" />
          <ExcelExportColumn field="preMessage" title="PreMessage" />
          <ExcelExportColumn field="form_id" title="Form ID" />
          <ExcelExportColumn field="form_type" title="Form Type" />
          <ExcelExportColumn
            field="isManualScoring"
            title="Is Manual Scoring"
          />
          <ExcelExportColumn field="question" title="Question" />
          <ExcelExportColumn field="score" title="Score" />
          <ExcelExportColumn field="answer" title="Answer" />
          <ExcelExportColumn field="isChecked" title="Is Checked" />
        </ExcelExport>
        <Grid
          style={{
            flexGrow: 1,
            minHeight: 0,
            height: '100%',
            overflow: 'hidden'
          }}
          pageable={DefaultGridSettings.pagerSettings}
          sortable={true}
          className={'cg-grid3'}
          dataItemKey={'id'}
          selectable={{
            enabled: true,
            drag: false,
            cell: false,
            mode: 'multiple'
          }}
          select={(filteredTaskContent ?? []).reduce(
            (acc, item) => ({ ...acc, [item.id]: item.selected }),
            {} as { [id: string]: boolean }
          )}
          onSelectionChange={onSelectionChange}
          onHeaderSelectionChange={onHeaderSelectionChange}
          data={process(filteredTaskContent ?? [], dataState)}
          {...dataState}
          onDataStateChange={(e: GridDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}>
          <Column
            filterable={false}
            field={SELECTED_FIELD}
            width={50}
            columnType="checkbox"
            headerSelectionValue={
              (filteredTaskContent?.length ?? 0) > 0 &&
              filteredTaskContent!.every((item) => item.selected)
            }
          />
          <Column
            field={'title'}
            title={'Title'}
            cells={{
              data: LinkedNameCell
            }}
          />
          <Column
            field={'description'}
            title={'Description'}
            cells={{
              data: DescriptionCell
            }}
          />
          <Column width={50} title={'Pre'} field={'preMessage'} />
          <Column width={60} title={'Intro'} field={'intro'} />
          <Column title={'Form'} field={'formCount'} width={60} />
          <Column width={50} title={'ODT'} field={'odt'} />
          <Column width={100} title={'ODTC'} field={'odtCategory'} />
          <Column width={50} title={'AR'} field={'ar'} />
          <Column cells={{ data: DeleteCell }} />
          <GridNoRecords>
            <NoRecords />
          </GridNoRecords>
        </Grid>
      </div>

      {showConfirmationDelete && (
        <YesNoDialog
          title="Confirm removal"
          onConfirm={(action) => {
            if (action === 'yes') {
              if (showConfirmationDelete.mode === 'single') {
                onDeleteEntity(showConfirmationDelete.id);
              } else if (showConfirmationDelete.mode === 'bulk') {
                onDeleteEntities(showConfirmationDelete.ids);
              }
            }
            setShowConfirmationDelete(null);
          }}
          onClose={() => setShowConfirmationDelete(null)}>
          {confirmationMessage}
        </YesNoDialog>
      )}

      {pendingImport && (
        <Window
          modal={true}
          minimizeButton={undefined}
          className={'shadow'}
          initialWidth={450}
          initialHeight={250}
          title={'Import Task Content'}
          onClose={handleCancelImport}>
          <div className={'p-3'}>
            <p>
              You are about to import{' '}
              <strong>{pendingImport.taskContents.length}</strong> task content
              item(s).
            </p>
            <Checkbox
              label={'Create linked Task from imported Task Content'}
              checked={importCreateTask}
              onChange={(e) => setImportCreateTask(e.value)}
            />
          </div>
          <WindowActionsBar>
            <Button themeColor={'secondary'} onClick={handleCancelImport}>
              Cancel
            </Button>
            <Button themeColor={'primary'} onClick={handleConfirmImport}>
              Import
            </Button>
          </WindowActionsBar>
        </Window>
      )}
    </>
  );
};

export default TaskContents;
